import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Modal, Button } from "react-bootstrap";
import axios from "axios";
import html2pdf from 'html2pdf.js';
import { UserContext } from "../../Context/UserContext";
import CurrencySymbol, { resolveCurrencyCode } from "../CurrencySymbol";
import "../Form/CreateCustomerPage.css";
const isElectron = false;
const ipcRenderer = null;
function Cash() {
  const location = useLocation();
  const navigate = useNavigate();
  const { baseUrl, getHeaders } = useContext(UserContext);

  const [billDetails, setBillDetails] = useState(null);
  const [rewardPointsData, setRewardPointsData] = useState(null);
  const [cashGiven, setCashGiven] = useState("");
  const [change, setChange] = useState(0);

  // New states for Reward Redemption
  const [customerPoints, setCustomerPoints] = useState(0);
  const [pointsToEarn, setPointsToEarn] = useState(0);
  const [pointsToRedeem, setPointsToRedeem] = useState("");
  const [rewardSettings, setRewardSettings] = useState({ conversion_rate: 1, currency: 'AED' });

  const [vatRate, setVatRate] = useState(0.2); // Default to 20% as per request
  const [showModal, setShowModal] = useState(false);
  const [emailAddress, setEmailAddress] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [warningMessage, setWarningMessage] = useState("");
  const [warningType, setWarningType] = useState("warning");
  const [pendingAction, setPendingAction] = useState(null);
  const [printSettings, setPrintSettings] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  // System settings state
  const [settings, setSettings] = useState({});
  const shortcutKey = `isKeyboardShortcutsEnabled_${localStorage.getItem('active_company') || 'Company'}_${localStorage.getItem('active_branch') || 'Branch'}`;
  const isKeyboardShortcutsEnabled = localStorage.getItem(shortcutKey) === "true";
  // Current time state for real-time updates (like OpeningEntry)
  const [currentTime, setCurrentTime] = useState(new Date());
  const API_URL = '';
  // Default print settings (used when no active settings fetched)
  const defaultPrintSettings = {
    headerBlocks: [
      { id: 'h1', text: 'My Restaurant', align: 'center', label: 'Restaurant Name' },
      { id: 'h2', text: '123 Store Street, City', align: 'center', label: 'Street' },
      { id: 'h3', text: 'Phone: +91 123-456-7890', align: 'center', label: 'Phone' },
      { id: 'h4', text: 'GSTIN: 12ABCDE3456F7Z8', align: 'center', label: 'GSTIN' },
    ],
    footerBlocks: [
      { id: 'f1', text: 'Thank You! Visit Again!', align: 'center', label: 'Thank You Message' },
      { id: 'f2', text: 'Powered by MyRestaurant', align: 'center', label: 'Powered By' },
    ]
  };
  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);
  // Fetch logo for preview
  const fetchLogo = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/logo`);
      if (response.data.logo) {
        setLogoUrl(API_URL + response.data.logo);
      }
    } catch (err) {
      console.error("Failed to fetch logo for preview:", err);
      setLogoUrl(null); // Ensure null if fetch fails
    }
  };
  // Fetch system settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/settings`);
        if (response.data) {
          setSettings(response.data);
          localStorage.setItem('systemSettings', JSON.stringify(response.data));
        }
      } catch (err) {
        console.error('Error fetching settings:', err);
        const stored = localStorage.getItem('systemSettings');
        if (stored) {
          setSettings(JSON.parse(stored));
        }
      }
    };
    fetchSettings();
  }, []);
  // Dynamic date formatting based on system settings (local time only)
  const getFormattedDate = (date, dateFormat) => {
    if (!dateFormat) {
      // Default: yyyy longmonth dd
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    }
    const numericFormatter = new Intl.DateTimeFormat('en', { year: 'numeric', month: '2-digit', day: 'numeric' });
    const parts = numericFormatter.formatToParts(date);
    const year = parts.find((p) => p.type === 'year')?.value || '';
    const month = parts.find((p) => p.type === 'month')?.value || '';
    const day = parts.find((p) => p.type === 'day')?.value || '';
    switch (dateFormat) {
      case 'dd-mm-yyyy':
        return `${day.padStart(2, '0')}-${month}-${year}`;
      case 'mm-dd-yyyy':
        return `${month}-${day.padStart(2, '0')}-${year}`;
      case 'yyyy-mm-dd':
        return `${year}-${month}-${day.padStart(2, '0')}`;
      case 'dd/mm/yyyy':
        return `${day.padStart(2, '0')}/${month}/${year}`;
      case 'mm/dd/yyyy':
        return `${month}/${day.padStart(2, '0')}/${year}`;
      case 'yyyy/mm/dd':
        return `${year}/${month}/${day.padStart(2, '0')}`;
      case 'yyyy-long-mm-dd':
        // yyyy longmonth dd, e.g., 2025 October 29
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        });
      case 'dd-MMM-yy':
        // dd-MMM-yy, e.g., 30-Oct-25
        const monthShort = date.toLocaleDateString('en-US', { month: 'short' });
        const dayStr = date.getDate().toString().padStart(2, '0');
        const yearShort = date.getFullYear().toString().slice(-2);
        return `${dayStr}-${monthShort}-${yearShort}`;
      default:
        // Fallback to yyyy longmonth dd
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    }
  };
  // Dynamic time formatting based on system settings (local time only, conditional seconds) - Fixed to match OpeningEntry logic
  const getFormattedTime = (date, timeFormat) => {
    if (!timeFormat) {
      // Default: 12-hour without seconds
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    }
    const hasSeconds = timeFormat.includes(':ss') || timeFormat.includes('ss');
    const is12Hour = timeFormat.includes(' a') || timeFormat.startsWith('hh');
    const options = {
      hour: '2-digit',
      minute: '2-digit',
      ...(hasSeconds && { second: '2-digit' }),
      hour12: is12Hour,
    };
    return date.toLocaleTimeString('en-US', options);
  };
  // Currency formatter for totals
  const getCurrencyFormatter = (currencyOverride = null, precisionOverride = null, asHtml = false, asReact = false) => {
    // Resolve currency from active context using currency_map if not explicitly provided
    const activeComp = localStorage.getItem('active_company') || '';
    const activeBranch = localStorage.getItem('active_branch') || '';
    const currency = currencyOverride || resolveCurrencyCode(null, activeComp, activeBranch);
    const precision = precisionOverride !== null ? parseInt(precisionOverride) : (parseInt(settings.currencyPrecision) || 2);

    return {
      format: (value) => {
        const numValue = parseFloat(value) || 0;
        const fixedValue = numValue.toFixed(precision);
        if (asReact) {
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
              <span style={{ marginRight: '2px', transform: 'translateY(1px)' }}>
                <CurrencySymbol currencyCode={currency} size={14} />
              </span>
              {fixedValue}
            </span>
          );
        } else if (asHtml) {
          if (currency?.toUpperCase() === 'AED') {
            const origin = typeof window !== 'undefined' ? window.location.origin : '';
            const timestamp = new Date().getTime();
            return `<img src="${origin}/assets/Dirham%20Currency%20Symbol%20-%20Black.svg?v=${timestamp}" alt="AED" style="height: 12px; width: auto; vertical-align: middle; margin-right: 2px;" /> ${fixedValue}`;
          } else {
            const symbols = {
              INR: "₹", USD: "$", EUR: "€", GBP: "£", JPY: "¥", CNY: "¥",
              SGD: "S$", MYR: "RM", THB: "฿", IDR: "Rp", KRW: "₩", PHP: "₱", SAR: "﷼",
              QAR: "﷼", KWD: "د.ك", OMR: "﷼", BHD: ".د.ب", CAD: "CA$", AUD: "A$", NZD: "NZ$",
              CHF: "CHF", ZAR: "R", BRL: "R$", PKR: "₨", LKR: "Rs", NGN: "₦"
            };
            return `${symbols[currency?.toUpperCase()] || currency} ${fixedValue}`;
          }
        } else {
          const symbols = {
            INR: "₹", USD: "$", EUR: "€", GBP: "£", AED: "د.إ", JPY: "¥", CNY: "¥",
            SGD: "S$", MYR: "RM", THB: "฿", IDR: "Rp", KRW: "₩", PHP: "₱", SAR: "﷼",
            QAR: "﷼", KWD: "د.ك", OMR: "﷼", BHD: ".د.ب", CAD: "CA$", AUD: "A$", NZD: "NZ$",
            CHF: "CHF", ZAR: "R", BRL: "R$", PKR: "₨", LKR: "Rs", NGN: "₦"
          };
          return `${symbols[currency?.toUpperCase()] || currency} ${fixedValue}`;
        }
      }
    };
  };

  // Dedicated formatters
  const formatCurrencyReact = (amount) => getCurrencyFormatter(null, 2, false, true).format(amount);
  const formatCurrencyHtml = (amount) => getCurrencyFormatter(null, 2, true, false).format(amount);
  const formatCurrencyString = (amount) => getCurrencyFormatter(null, 2, false, false).format(amount);
  // CSS Styles (Updated with improved design: modern gradients, shadows, transitions, responsive adjustments)
  const styles = `
    .strikethroughStyle {
      text-decoration: line-through;
    }
    .cash-card-override {
       width: 100% !important;
       max-width: 750px !important;
       margin-left: auto !important;
       margin-right: auto !important;
    }
  `;
  // Fetch active print settings
  useEffect(() => {
    const fetchPrintSettings = async () => {
      try {
        const response = await axios.get("/api/print_settings/active");
        setPrintSettings(response.data);
      } catch (err) {
        console.error("Failed to fetch active print settings:", err);
        // Use default if fetch fails
        setPrintSettings(defaultPrintSettings);
      }
    };
    fetchPrintSettings();
  }, []);
  // Fetch logo
  useEffect(() => {
    fetchLogo();
  }, []);
  // Initialize bill details from location state
  useEffect(() => {
    if (location.state?.rewardPointsData) {
      setRewardPointsData(location.state.rewardPointsData);
    }
    if (location.state?.billDetails) {
      const now = new Date();
      const defaultTime = now.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false
      });
      const formattedBillDetails = {
        ...location.state.billDetails,
        subtotal: location.state.billDetails.subtotal || 0,
        vat_amount: location.state.billDetails.vat_amount || 0,
        totalAmount: location.state.billDetails.totalAmount || 0,
        invoice_no: location.state.billDetails.invoice_no || `INV-${Date.now()}`,
        customerName: location.state.billDetails.customerName || location.state.billDetails.customer || "N/A",
        customerGroup: location.state.billDetails.customerGroup || location.state.billDetails.customer_group || "N/A",
        phoneNumber: location.state.billDetails.phoneNumber || location.state.billDetails.phone || "N/A",
        email: location.state.billDetails.email || "N/A",
        whatsappNumber: location.state.billDetails.whatsappNumber || "N/A",
        tableNumber: location.state.billDetails.tableNumber || "N/A",
        deliveryAddress: location.state.billDetails.deliveryAddress || {
          building_name: "",
          flat_villa_no: "",
          country: "",
          field1: "",
          field2: "",
          field3: "",
        },
        date: location.state.billDetails.date || now.toISOString().split("T")[0],
        time: location.state.billDetails.time || defaultTime,
        payments: location.state.billDetails.payments || [{ mode_of_payment: "CASH" }],
        items: location.state.billDetails.items.map((item) => ({
          ...item,
          item_name: item.item_name || item.name || "Unnamed Item",
          quantity: Number(item.quantity) || 1,
          basePrice: Number(item.basePrice) || 0,
          originalBasePrice: item.originalBasePrice || null,
          totalPrice: Number(item.totalPrice) || Number(item.basePrice) * Number(item.quantity) || 0,
          selectedSize: item.selectedSize || null,
          icePreference: item.icePreference || "without_ice",
          icePrice: Number(item.ice_price) || 0,
          isSpicy: item.isSpicy || false,
          spicyPrice: item.isSpicy ? Number(item.spicy_price) || 20.00 : 0,
          addonQuantities: item.addonQuantities || {},
          addonVariants: item.addonVariants || {},
          addonPrices: item.addonPrices || {},
          addonSizePrices: item.addonSizePrices || {},
          addonSpicyPrices: item.addonSpicyPrices || {},
          addonImages: item.addonImages || {},
          comboQuantities: item.comboQuantities || {},
          comboVariants: item.comboVariants || {},
          comboPrices: item.comboPrices || {},
          comboSizePrices: item.comboSizePrices || {},
          comboSpicyPrices: item.comboSpicyPrices || {},
          comboImages: item.comboImages || {},
          selectedCombos: item.selectedCombos || [],
          kitchen: item.kitchen || "Main Kitchen",
          ingredients: item.ingredients || [],
          selectedCustomVariants: item.selectedCustomVariants || {},
          customVariantsDetails: item.customVariantsDetails || {},
          customVariantsQuantities: item.customVariantsQuantities || {},
          itemToppings: item.itemToppings || {},
          addons:
            item.addons?.map((addon) => {
              const cvs = addon.custom_variants || {};
              const tops = addon.addon_toppings || {};
              // Use addon_size_price if available (pure base price), otherwise fallback to addon_price
              const basePrice = addon.addon_size_price !== undefined
                ? Number(addon.addon_size_price)
                : (Number(addon.addon_price) || 0);

              return {
                addon_name: addon.name1,
                addon_quantity: Number(addon.addon_quantity) || 0,
                addon_price: basePrice,
                addon_total_price: Number(addon.addon_total_price) || basePrice * Number(addon.addon_quantity),
                size: addon.size || "M",
                isSpicy: addon.isSpicy || false,
                spicyPrice: Number(addon.addon_spicy_price) || Number(addon.spicyPrice) || 0,
                kitchen: addon.kitchen || "Main Kitchen",
                addon_image: addon.addon_image || "/static/images/default-addon-image.jpg",
                custom_variants: cvs,
                addon_toppings: tops,
              };
            }) || [],
          combos:
            item.selectedCombos?.map((combo) => {
              const cvs = combo.custom_variants || {};
              const tops = combo.combo_toppings || {};
              // Use combo_size_price if available (pure base price), otherwise fallback to combo_price
              const basePrice = combo.combo_size_price !== undefined
                ? Number(combo.combo_size_price)
                : (Number(combo.combo_price) || 0);

              return {
                name1: combo.name1,
                combo_price: basePrice,
                combo_total_price: Number(combo.combo_total_price) || basePrice * Number(combo.combo_quantity),
                size: combo.size || "M",
                combo_quantity: Number(combo.combo_quantity) || 1,
                isSpicy: combo.isSpicy || false,
                spicyPrice: Number(combo.combo_spicy_price) || Number(combo.spicyPrice) || 0,
                kitchen: combo.kitchen || "Main Kitchen",
                combo_image: combo.combo_image || "/static/images/default-combo-image.jpg",
                custom_variants: cvs,
                combo_toppings: tops,
              };
            }) || [],
          isCombo: item.isCombo || false,
          comboItems: item.comboItems?.map((citem) => ({
            name: citem.name,
            description: citem.description || "",
            price: Number(citem.price) || 0,
            image: citem.image,
            kitchen: citem.kitchen,
          })) || [],
        })),
      };
      setBillDetails(formattedBillDetails);
      setEmailAddress(formattedBillDetails.email);
    }
  }, [location.state]);

  // Fetch Reward Settings and Customer Points
  useEffect(() => {
    const fetchPointsData = async () => {
      try {
        const active_company = localStorage.getItem('active_company') || '';
        const active_branch = localStorage.getItem('active_branch') || '';

        // Fetch Settings
        const settingsRes = await axios.get(`${baseUrl}/api/reward-settings`, { headers: getHeaders(active_branch, active_company) });
        if (settingsRes.data && settingsRes.data.success && settingsRes.data.data) {
          setRewardSettings({
            conversion_rate: settingsRes.data.data.point_conversion_rate || settingsRes.data.data.conversion_rate || 1,
            currency: settingsRes.data.data.currency || 'AED'
          });
        } else if (Array.isArray(settingsRes.data) && settingsRes.data.length > 0) {
          setRewardSettings({
            conversion_rate: settingsRes.data[0].conversion_rate || 1,
            currency: settingsRes.data[0].currency || 'AED'
          });
        }

        // Fetch Customer Points
        const phone = billDetails?.phoneNumber || location.state?.payload?.phoneNumber || location.state?.payload?.phone;
        if (phone && phone !== "N/A" && phone.trim() !== "" && phone.toLowerCase() !== "walk-in" && phone.toLowerCase() !== "cash") {
          const customerRes = await axios.get(`${baseUrl}/api/customers`, { headers: getHeaders(active_branch, active_company) });
          const cust = customerRes.data.find(c => c.phone_number === phone);
          if (cust) {
            setCustomerPoints(parseFloat(cust.available_points || 0));
          }

          // Calculate Points to Earn for this order
          try {
            const calcPayload = {
              customer_phone: phone,
              items: location.state?.payload?.items || billDetails?.items || [],
              net_total: location.state?.payload?.net_total || billDetails?.totalAmount || 0,
              company: active_company,
              branch: active_branch
            };
            const calcRes = await axios.post(`${baseUrl}/api/calculate-rewards`, calcPayload, { headers: getHeaders(active_branch, active_company) });
            if (calcRes.data && calcRes.data.points_earned !== undefined) {
              setPointsToEarn(calcRes.data.points_earned);
            }
          } catch (calcError) {
            console.error("Error calculating points to earn:", calcError);
          }
        }
      } catch (error) {
        console.error("Error fetching reward data for checkout:", error);
      }
    };
    if (billDetails?.phoneNumber || location.state?.payload?.phoneNumber) {
      fetchPointsData();
    }
  }, [billDetails?.phoneNumber, location.state?.payload]);

  // Fetch VAT rate - If API fails, keep default 20%
  useEffect(() => {
    const fetchVat = async () => {
      try {
        const response = await axios.get('/api/get-vat');
        setVatRate(response.data.vat / 100);
      } catch (error) {
        console.error('Failed to fetch VAT:', error);
        // Keep default 0.2 if fetch fails
      }
    };
    fetchVat();
  }, []);
  // Auto-close modal and navigate after 100 seconds
  useEffect(() => {
    let timer;
    if (showModal) {
      timer = setTimeout(() => {
        setShowModal(false);
        navigate("/frontpage");
      }, 100000);
    }
    return () => clearTimeout(timer);
  }, [showModal, navigate]);
  // Calculate item prices including addons, combos, custom variants, and extras
  const calculateItemPrices = (item) => {
    if (item.isCombo) {
      return {
        basePrice: Number(item.basePrice) || 0,
        icePrice: 0,
        spicyPrice: 0,
        addonTotal: 0,
        comboTotal: 0,
        customVariantsTotal: 0,
        itemToppingsTotal: 0,
        totalAmount: (Number(item.basePrice) || 0) * item.quantity,
      };
    }
    const basePrice = Number(item.basePrice) || 0;
    const icePrice = item.icePreference === "with_ice" ? Number(item.icePrice) || 0 : 0;
    const spicyPrice = item.isSpicy ? Number(item.spicyPrice) || 0 : 0;
    const addonTotal =
      item.addons && item.addons.length > 0
        ? item.addons.reduce(
          (sum, addon) => sum + Number(addon.addon_total_price) * addon.addon_quantity,
          0
        )
        : 0;
    const comboTotal =
      item.combos && item.combos.length > 0
        ? item.combos.reduce(
          (sum, combo) => sum + Number(combo.combo_total_price) * combo.combo_quantity,
          0
        )
        : 0;
    const customVariantsTotal = item.customVariantsDetails
      ? Object.values(item.customVariantsDetails).reduce(
        (sum, variant) => sum + (Number(variant.price) || 0) * (item.customVariantsQuantities?.[variant.name] || 1),
        0
      ) * item.quantity
      : 0;
    const itemToppingsTotal = item.itemToppings
      ? Object.values(item.itemToppings).reduce(
        (sum, t) => sum + (Number(t.price) || 0) * (Number(t.qty) || 1),
        0
      )
      : 0;
    const totalAmount = (basePrice + icePrice + spicyPrice + customVariantsTotal + itemToppingsTotal) * item.quantity + addonTotal + comboTotal;
    return { basePrice, icePrice, spicyPrice, addonTotal, comboTotal, customVariantsTotal, itemToppingsTotal, totalAmount };
  };
  // Get display name for items
  const getItemDisplayName = (item) => {
    const sizeDisplay = item.selectedSize ? ` (${item.selectedSize})` : "";
    return `${item.item_name}${sizeDisplay}`;
  };
  // FIXED: Use passed subtotal (excl total)
  const calculateSubtotal = () => {
    return billDetails?.subtotal || 0;
  };
  // FIXED: Aggregate VAT by rate from items.taxBreakdown
  const getVatByRate = () => {
    const byRate = {};
    billDetails?.items.forEach(item => {
      if (item.taxBreakdown) {
        Object.entries(item.taxBreakdown).forEach(([rate, amt]) => {
          byRate[rate] = (byRate[rate] || 0) + amt;
        });
      }
    });
    return byRate;
  };
  // FIXED: Use passed vat_amount
  const calculateVAT = () => {
    return billDetails?.vat_amount || 0;
  };

  // Calculate Points Discount
  const calculatePointsDiscount = () => {
    const points = parseFloat(pointsToRedeem) || 0;
    return points * parseFloat(rewardSettings.conversion_rate);
  };

  // FIXED: Use passed totalAmount (incl) and subtract points discount
  const calculateGrandTotal = () => {
    const baseTotal = billDetails?.totalAmount || 0;
    return Math.max(0, baseTotal - calculatePointsDiscount());
  };
  // Parse bill date and time to Date object
  const parseBillDateTime = (dateStr, timeStr, timeZone) => {
    // Assume dateStr is 'YYYY-MM-DD', timeStr is 'HH:mm' or similar
    // Ensure timeStr is in HH:mm format (24-hour)
    let cleanTimeStr = timeStr;
    if (timeStr.includes(':')) {
      // If it has AM/PM, parse to 24-hour (simple check, assuming standard format)
      const [timePart, period] = timeStr.split(' ');
      if (period && (period.toUpperCase() === 'AM' || period.toUpperCase() === 'PM')) {
        let [hours, minutes] = timePart.split(':');
        hours = parseInt(hours, 10);
        minutes = parseInt(minutes, 10);
        if (period.toUpperCase() === 'PM' && hours !== 12) {
          hours += 12;
        } else if (period.toUpperCase() === 'AM' && hours === 12) {
          hours = 0;
        }
        cleanTimeStr = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
      }
    }
    const fullDateStr = `${dateStr}T${cleanTimeStr}:00.000Z`; // Add Z for UTC
    const date = new Date(fullDateStr);
    if (isNaN(date.getTime())) {
      // Fallback to current date/time
      return new Date();
    }
    return date;
  };
  // Handle cash input change
  const handleCashChange = (e) => {
    const givenAmount = e.target.value === "" ? "" : Number(e.target.value);
    setCashGiven(givenAmount);
    if (givenAmount !== "" && !isNaN(givenAmount)) {
      const grandTotal = calculateGrandTotal();
      setChange(Number(givenAmount - grandTotal));
    } else {
      setChange(0);
    }
  };
  // Handle warning modal OK button
  const handleWarningOk = () => {
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
    setWarningMessage("");
    setWarningType("warning");
  };
  const saveSaleIfRequired = async () => {
    let finalInvoiceNo = billDetails?.invoice_no;
    const { payload, frontPageState } = location.state || {};

    if (payload) {
      // Append redemption details to payload
      payload.redeemed_points = parseFloat(pointsToRedeem) || 0;
      payload.redeemed_amount = calculatePointsDiscount();
      payload.grand_total = calculateGrandTotal();
      payload.invoice_currency = resolveCurrencyCode(null, localStorage.getItem('active_company'), localStorage.getItem('active_branch')) || rewardSettings.currency || 'AED';

      // Add cash details to the payment payload
      if (payload.payments && payload.payments.length > 0) {
        payload.payments[0].cash_given = cashGiven || payload.grand_total;
        payload.payments[0].change_returned = change || 0;
      }

      let response;
      if (finalInvoiceNo && !finalInvoiceNo.startsWith('INV-')) {
        const apiPath = baseUrl ? `${baseUrl}/api/sales/${finalInvoiceNo}` : `/api/sales/${finalInvoiceNo}`;
        response = await axios.put(apiPath, payload, { headers: getHeaders() });
      } else {
        const apiPath = baseUrl ? `${baseUrl}/api/sales` : '/api/sales';
        response = await axios.post(apiPath, payload, { headers: getHeaders() });
        finalInvoiceNo = response.data.invoice_no;
        setBillDetails(prev => ({ ...prev, invoice_no: finalInvoiceNo }));
      }

      const orderId = frontPageState?.orderId;
      if (orderId) {
        try {
          const apiPath = baseUrl ? `${baseUrl}/api/activeorders/${orderId}` : `/api/activeorders/${orderId}`;
          const markedCartItems = frontPageState.cartItems.map(item => ({ ...item, isPaid: true }));
          await axios.put(apiPath, { paid: true, cartItems: markedCartItems }, { headers: getHeaders() });
        } catch (error) {
          console.error("Error updating order paid status:", error);
        }
      }

      // Clear payload from state so we don't save twice
      navigate(location.pathname, { state: { ...location.state, payload: null }, replace: true });
    }
    return finalInvoiceNo;
  };

  // Confirm payment and validate cash given
  const handlePaymentConfirm = async () => {
    setIsLoading(true);
    try {
      await saveSaleIfRequired();
      setIsLoading(false);
      setShowModal(true);
    } catch (error) {
      setIsLoading(false);
      setWarningMessage(`Failed to save sale: ${error.message}`);
      setWarningType("warning");
    }
  };
  // Format numbers for display using currency formatter
  const formatCurrency = (amount) => {
    return formatCurrencyReact(Number(amount));
  };
  // Helper to format delivery address in the specified order (single line for UI)
  const formatDeliveryAddress = (deliveryAddress) => {
    if (!deliveryAddress) return null;
    const parts = [
      deliveryAddress.flat_villa_no || "",
      deliveryAddress.building_name || "",
      deliveryAddress.field3 || "",
      deliveryAddress.field2 || "",
      deliveryAddress.field1 || "",
      deliveryAddress.country || ""
    ].filter(part => part.trim() !== ""); // Filter out empty parts
    return parts.length > 0 ? parts.join(", ") : null;
  };
  // Helper to format delivery address for print (multi-line HTML)
  const getPrintDeliveryAddressHtml = (deliveryAddress) => {
    if (!deliveryAddress) return null;
    const lines = [];
    // Line 1: flat_villa_no + building_name
    const line1 = [deliveryAddress.flat_villa_no, deliveryAddress.building_name].filter(Boolean).join(', ');
    if (line1) lines.push(line1);
    // Line 2: field3 + field2
    const line2 = [deliveryAddress.field3, deliveryAddress.field2].filter(Boolean).join(', ');
    if (line2) lines.push(line2);
    // Line 3: field1 + country
    const line3 = [deliveryAddress.field1, deliveryAddress.country].filter(Boolean).join(', ');
    if (line3) lines.push(line3);
    return lines.length > 0 ? lines.join('<br>') : null;
  };
  // Check if delivery address is available
  const hasDeliveryAddress = billDetails?.deliveryAddress &&
    (billDetails.deliveryAddress.building_name ||
      billDetails.deliveryAddress.flat_villa_no ||
      billDetails.deliveryAddress.country ||
      billDetails.deliveryAddress.field1 ||
      billDetails.deliveryAddress.field2 ||
      billDetails.deliveryAddress.field3);
  const formattedDeliveryAddress = formatDeliveryAddress(billDetails?.deliveryAddress);
  // Generate printable content as HTML
  const generatePrintableContent = (invoiceNoOverride = null, isPreview = false) => {
    if (!billDetails) return "";
    const subtotal = calculateSubtotal();
    const vatAmount = calculateVAT();
    const grandTotal = calculateGrandTotal();
    // Use currentTime for date and time in receipt (real-time like OpeningEntry)
    const formattedDate = getFormattedDate(currentTime, settings.dateFormat);
    const formattedTime = getFormattedTime(currentTime, settings.timeFormat);
    const deliveryAddressHtml = getPrintDeliveryAddressHtml(billDetails.deliveryAddress);
    const borderStyle = isPreview ? "border: none;" : "border: 1px solid #000000;";
    const effectivePrintSettings = printSettings || defaultPrintSettings;
    const headerHtml = (effectivePrintSettings.headerBlocks || []).map(b => `<div style="text-align: ${b.align};">${b.text || ''}</div>`).join('');
    const footerHtml = (effectivePrintSettings.footerBlocks || []).map(b => `<div style="text-align: ${b.align};">${b.text || ''}</div>`).join('');
    const formatter = { format: formatCurrencyHtml }; // Use updated formatter for consistent currency display
    const cashGivenDisplay =
      cashGiven && !isNaN(cashGiven) && Number(cashGiven) > 0
        ? `
            <tr>
              <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">Cash Given:</td>
              <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">${formatter.format(cashGiven)}</td>
            </tr>
            <tr>
              <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">Change Returned:</td>
              <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; color: ${change < 0 ? 'red' : 'inherit'};">${formatter.format(change)}</td>
            </tr>
          `
        : "";
    const offerRows = billDetails.items.filter(item => item.originalBasePrice).map(item => {
      return `
        <tr>
          <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">${item.item_name}:</td>
          <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;"><span style="text-decoration: line-through;">${formatter.format(item.originalBasePrice * item.quantity)}</span> ${formatter.format(item.basePrice * item.quantity)}</td>
        </tr>
      `;
    }).join('');
    const vatRows = Object.entries(getVatByRate()).map(([rate, amt]) => `
      <tr>
        <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">VAT (${rate}%):</td>
        <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">${formatter.format(amt)}</td>
      </tr>
    `).join('');
    const pointsRow = parseFloat(pointsToRedeem) > 0 ? `
      <tr>
        <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; color: #15803d; font-weight: bold;">Points Redeemed (${parseFloat(pointsToRedeem)} pts):</td>
        <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; color: #15803d; font-weight: bold;">-${formatter.format(calculatePointsDiscount())}</td>
      </tr>
    ` : '';
    return `
      <div style="font-family: 'Dubai Regular', Arial, sans-serif; width: 88mm; margin: 0 auto; font-size: 12px; padding: 10px; color: #000000; ${borderStyle} box-sizing: border-box; line-height: 1.2;">
        <div style="display: flex; flex-direction: column; align-items: center; text-align: center; margin-bottom: 15px;">
          ${logoUrl ? `<div style="margin-bottom: 5px;"><img src="${logoUrl}" alt="Logo" style="max-width: 50px; max-height: 50px; object-fit: contain; border-radius: 3px;"/></div>` : ''}
          <div style="font-family: 'Dubai Regular', Arial, sans-serif; font-size: 12px; width: 100%;">
            ${headerHtml}
          </div>
        </div>
        <table style="width: 100%; border-collapse: collapse; border: none; margin-bottom: 10px;">
          <tbody>
            <tr>
              <td style="width: 50%; text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">Invoice No</td>
              <td style="text-align: center; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">:</td>
              <td style="width: 50%; text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; white-space: nowrap;">${invoiceNoOverride || billDetails.invoice_no || "N/A"}</td>
            </tr>
            <tr>
              <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">Customer</td>
              <td style="text-align: center; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">:</td>
              <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; word-break: break-all;">${billDetails.customerName || "N/A"}</td>
            </tr>
            <tr>
              <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">Phone</td>
              <td style="text-align: center; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">:</td>
              <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; word-break: break-all;">${billDetails.phoneNumber || "N/A"}</td>
            </tr>
            <tr>
              <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">Email</td>
              <td style="text-align: center; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">:</td>
              <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; word-break: break-all;">${billDetails.email || "N/A"}</td>
            </tr>
            <tr>
              <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">WhatsApp</td>
              <td style="text-align: center; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">:</td>
              <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; word-break: break-all;">${billDetails.whatsappNumber || "N/A"}</td>
            </tr>
            ${billDetails.tableNumber && billDetails.tableNumber !== "N/A"
        ? `
                  <tr>
                    <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">Table</td>
                    <td style="text-align: center; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">:</td>
                    <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; word-break: break-all;">${billDetails.tableNumber}</td>
                  </tr>
                `
        : ""
      }
            ${hasDeliveryAddress && deliveryAddressHtml
        ? `
                  <tr>
                    <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; vertical-align: top;">Delivery Address</td>
                    <td style="text-align: center; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">:</td>
                    <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; white-space: pre-line; word-break: break-all;">${deliveryAddressHtml}</td>
                  </tr>
                `
        : ""
      }
            <tr>
              <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">Payment Mode</td>
              <td style="text-align: center; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">:</td>
              <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; word-break: break-all;">${billDetails.payments?.[0]?.mode_of_payment || "CASH"}</td>
            </tr>
            <tr>
              <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">Date</td>
              <td style="text-align: center; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">:</td>
              <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; white-space: nowrap;">${formattedDate}</td>
            </tr>
            <tr>
              <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">Time</td>
              <td style="text-align: center; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">:</td>
              <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; white-space: nowrap;">${formattedTime}</td>
            </tr>
          </tbody>
        </table>
        <table style="width: 100%; margin-bottom: 10px; border-collapse: collapse; border: 1px solid #000000; table-layout: fixed;">
          <thead>
            <tr style="border-bottom: 1px dashed #000000;">
              <th style="text-align: left; width: 40%; padding: 4px 8px; border: none; font-size: 12px; font-weight: bold;">Item</th>
              <th style="text-align: center; width: 15%; padding: 4px 8px; border: none; font-size: 12px; font-weight: bold;">Qty</th>
              <th style="text-align: right; width: 20%; padding: 4px 8px; border: none; font-size: 12px; font-weight: bold;">Price</th>
              <th style="text-align: right; width: 25%; padding: 4px 8px; border: none; font-size: 12px; font-weight: bold;">Total</th>
            </tr>
          </thead>
          <tbody>
            ${billDetails.items
        .map((item) => {
          const { basePrice, icePrice, spicyPrice, addonTotal, comboTotal } = calculateItemPrices(item);
          return `
                  <tr>
                    <td style="text-align: left; padding: 4px 8px; border-bottom: 1px solid #000; line-height: 1.2; font-size: 12px; vertical-align: top;">${getItemDisplayName(item)}</td>
                    <td style="text-align: center; padding: 4px 8px; border-bottom: 1px solid #000; line-height: 1.2; font-size: 12px;">${item.quantity}</td>
                    <td style="text-align: right; padding: 4px 8px; border-bottom: 1px solid #000; line-height: 1.2; font-size: 12px;">${formatter.format(basePrice)}</td>
                    <td style="text-align: right; padding: 4px 8px; border-bottom: 1px solid #000; line-height: 1.2; font-size: 12px;">${formatter.format(basePrice * item.quantity)}</td>
                  </tr>
                  ${item.comboItems && item.comboItems.length > 0
              ? item.comboItems
                .map(
                  (comboItem) => `
                              <tr>
                                <td style="text-align: left; padding: 2px 8px 2px 16px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px; color: #666; vertical-align: top;">+ ${comboItem.name}</td>
                                <td style="text-align: center; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;"></td>
                                <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;"></td>
                                <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;"></td>
                              </tr>
                            `
                )
                .join("")
              : ""
            }
                  ${item.icePreference === "with_ice" && icePrice > 0
              ? `
                        <tr>
                          <td style="text-align: left; padding: 2px 8px 2px 16px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px; color: #666; vertical-align: top;">+ Ice</td>
                          <td style="text-align: center; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${item.quantity}</td>
                          <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatter.format(icePrice)}</td>
                          <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatter.format(icePrice * item.quantity)}</td>
                        </tr>
                      `
              : ""
            }
                  ${item.isSpicy && spicyPrice > 0
              ? `
                        <tr>
                          <td style="text-align: left; padding: 2px 8px 2px 16px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px; color: #666; vertical-align: top;">+ Spicy</td>
                          <td style="text-align: center; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${item.quantity}</td>
                          <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatter.format(spicyPrice)}</td>
                          <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatter.format(spicyPrice * item.quantity)}</td>
                        </tr>
                      `
              : ""
            }
                  ${item.customVariantsDetails && Object.keys(item.customVariantsDetails).length > 0
              ? Object.entries(item.customVariantsDetails)
                .map(
                  ([variantName, variant]) => `
                            <tr>
                              <td style="text-align: left; padding: 2px 8px 2px 16px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px; color: #666; vertical-align: top;">+ ${variant.heading}: ${variant.name}</td>
                              <td style="text-align: center; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${item.customVariantsQuantities?.[variantName] || 1}</td>
                              <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatter.format(variant.price)}</td>
                              <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatter.format(variant.price * (item.customVariantsQuantities?.[variantName] || 1))}</td>
                            </tr>
                          `
                )
                .join("")
                .join("")
              : ""
            }
                  ${item.itemToppings && Object.keys(item.itemToppings).length > 0
              ? Object.entries(item.itemToppings)
                .map(
                  ([name, top]) => `
                            <tr>
                              <td style="text-align: left; padding: 2px 8px 2px 16px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px; color: #666; vertical-align: top;">+ ${top.name} (Topping)</td>
                              <td style="text-align: center; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${item.quantity * (top.qty || 1)}</td>
                              <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatter.format(top.price)}</td>
                              <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatter.format(top.price * item.quantity * (top.qty || 1))}</td>
                            </tr>
                          `
                )
                .join("")
              : ""
            }
                  ${item.addons && item.addons.length > 0
              ? item.addons
                .map(
                  (addon) =>
                    addon.addon_quantity > 0
                      ? `
                                  <tr>
                                    <td style="text-align: left; padding: 4px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 12px; font-weight: bold; vertical-align: top;">${addon.addon_name}${addon.size ? ` (${addon.size})` : ""}</td>
                                    <td style="text-align: center; padding: 4px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 12px; font-weight: bold;">${addon.addon_quantity}</td>
                                    <td style="text-align: right; padding: 4px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 12px; font-weight: bold;">${formatter.format(addon.addon_price)}</td>
                                    <td style="text-align: right; padding: 4px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 12px; font-weight: bold;">${formatter.format(addon.addon_price * addon.addon_quantity)}</td>
                                  </tr>
                                  ${addon.isSpicy && addon.spicyPrice > 0
                        ? `
                                        <tr>
                                          <td style="text-align: left; padding: 2px 8px 2px 24px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 10px; color: #666; vertical-align: top;">+ Spicy</td>
                                          <td style="text-align: center; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 10px;">${addon.addon_quantity}</td>
                                          <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 10px;">${formatter.format(addon.spicyPrice)}</td>
                                          <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 10px;">${formatter.format(addon.spicyPrice * addon.addon_quantity)}</td>
                                        </tr>
                                      `
                        : ""
                      }
                                  ${addon.custom_variants && Object.keys(addon.custom_variants).length > 0
                        ? Object.entries(addon.custom_variants)
                          .map(
                            ([cvName, cv]) => `
                                          <tr>
                                            <td style="text-align: left; padding: 2px 8px 2px 24px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px; color: #666; vertical-align: top;">+ ${cv.heading || cvName}: ${cv.name}</td>
                                            <td style="text-align: center; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${addon.addon_quantity}</td>
                                            <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatter.format(cv.price)}</td>
                                            <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatter.format(cv.price * addon.addon_quantity)}</td>
                                          </tr>
                                        `
                          )
                          .join("")
                        : ""
                      }
                                  ${addon.addon_toppings && Object.keys(addon.addon_toppings).length > 0
                        ? Object.entries(addon.addon_toppings)
                          .map(
                            ([topName, top]) => `
                                          <tr>
                                            <td style="text-align: left; padding: 2px 8px 2px 24px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px; color: #666; vertical-align: top;">+ ${top.name} (Topping)</td>
                                            <td style="text-align: center; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${addon.addon_quantity * (top.qty || 1)}</td>
                                            <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatter.format(top.price)}</td>
                                            <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatter.format(top.price * addon.addon_quantity * (top.qty || 1))}</td>
                                          </tr>
                                        `
                          )
                          .join("")
                        : ""
                      }
                                `
                      : ""
                )
                .join("")
              : ""
            }
                  ${item.combos && item.combos.length > 0
              ? item.combos
                .map(
                  (combo) =>
                    combo.combo_quantity > 0
                      ? `
                                  <tr>
                                    <td style="text-align: left; padding: 4px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 12px; font-weight: bold; vertical-align: top;">${combo.name1}${combo.size ? ` (${combo.size})` : ""}</td>
                                    <td style="text-align: center; padding: 4px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 12px; font-weight: bold;">${combo.combo_quantity}</td>
                                    <td style="text-align: right; padding: 4px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 12px; font-weight: bold;">${formatter.format(combo.combo_price)}</td>
                                    <td style="text-align: right; padding: 4px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 12px; font-weight: bold;">${formatter.format(combo.combo_price * combo.combo_quantity)}</td>
                                  </tr>
                                  ${combo.isSpicy && combo.spicyPrice > 0
                        ? `
                                        <tr>
                                          <td style="text-align: left; padding: 2px 8px 2px 24px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 10px; color: #666; vertical-align: top;">+ Spicy</td>
                                          <td style="text-align: center; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 10px;">${combo.combo_quantity}</td>
                                          <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 10px;">${formatter.format(combo.spicyPrice)}</td>
                                          <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 10px;">${formatter.format(combo.spicyPrice * combo.combo_quantity)}</td>
                                        </tr>
                                      `
                        : ""
                      }
                                  ${combo.custom_variants && Object.keys(combo.custom_variants).length > 0
                        ? Object.entries(combo.custom_variants)
                          .map(
                            ([cvName, cv]) => `
                                          <tr>
                                            <td style="text-align: left; padding: 2px 8px 2px 24px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px; color: #666; vertical-align: top;">+ ${cv.heading || cvName}: ${cv.name}</td>
                                            <td style="text-align: center; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${combo.combo_quantity}</td>
                                            <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatter.format(cv.price)}</td>
                                            <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatter.format(cv.price * combo.combo_quantity)}</td>
                                          </tr>
                                        `
                          )
                          .join("")
                        : ""
                      }
                                  ${combo.combo_toppings && Object.keys(combo.combo_toppings).length > 0
                        ? Object.entries(combo.combo_toppings)
                          .map(
                            ([topName, top]) => `
                                          <tr>
                                            <td style="text-align: left; padding: 2px 8px 2px 24px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px; color: #666; vertical-align: top;">+ ${top.name} (Topping)</td>
                                            <td style="text-align: center; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${combo.combo_quantity * (top.qty || 1)}</td>
                                            <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatter.format(top.price)}</td>
                                            <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatter.format(top.price * combo.combo_quantity * (top.qty || 1))}</td>
                                          </tr>
                                        `
                          )
                          .join("")
                        : ""
                      }
                                `
                      : ""
                )
                .join("")
              : ""
            }
                `;
        })
        .join("")}
          </tbody>
        </table>
        <table style="width: 100%; border-collapse: collapse; border: none; margin-bottom: 10px;">
          <tbody>
            <tr>
              <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">Total Quantity:</td>
              <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">${billDetails.items.reduce((sum, item) => sum + item.quantity, 0)}</td>
            </tr>
            ${offerRows}
            <tr>
              <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; font-weight: bold;">Subtotal:</td>
              <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; font-weight: bold;">${formatter.format(subtotal)}</td>
            </tr>
            ${vatRows}
            <tr>
              <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">Total VAT:</td>
              <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">${formatter.format(vatAmount)}</td>
            </tr>
            ${pointsRow}
            ${cashGivenDisplay}
            <tr style="font-weight: bold; border-top: 2px solid #000;">
              <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 14px;">Grand Total:</td>
              <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 14px;">${formatter.format(grandTotal)}</td>
            </tr>
          </tbody>
        </table>
        <div style="margin-top: 15px; width: 100%;">
          ${footerHtml}
        </div>
      </div>
    `;
  };
  // Handle print functionality
  const handlePrint = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const content = generatePrintableContent(billDetails?.invoice_no);

      if (isElectron && ipcRenderer) {
        ipcRenderer.send("open-print-preview", content);
        ipcRenderer.once("print-preview-response", (event, response) => {
          setIsLoading(false);
          if (response.success) {
            setShowModal(false);
            navigate("/frontpage");
          } else {
            setError("Failed to open print preview: " + response.error);
            setWarningMessage("Print preview failed: " + response.error);
            setWarningType("warning");
          }
        });
      } else {
        const win = window.open("", "_blank");
        win.document.write(`
          <html>
            <head>
              <title>Receipt - Invoice ${billDetails?.invoice_no || "N/A"}</title>
              <style>
                @media print {
                  body { margin: 0; }
                  @page { margin: 0; size: 88mm auto; }
                }
                body { margin: 0; }
              </style>
            </head>
            <body>
              ${content}
            </body>
          </html>
        `);
        win.document.close();
        win.focus();
        win.print();
        win.close();
        setIsLoading(false);
        setShowModal(false);
        navigate("/frontpage");
      }
    } catch (err) {
      setIsLoading(false);
      setError("Printing failed: " + err.message);
      setWarningMessage("Printing failed: " + err.message);
      setWarningType("warning");
    }
  };
  // Handle email functionality
  const handleEmail = async () => {
    if (!emailAddress || !emailAddress.includes("@")) {
      setWarningMessage("Please enter a valid email address!");
      setWarningType("warning");
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      let htmlContent = generatePrintableContent(billDetails?.invoice_no);
      const originalHtmlContent = htmlContent;
      const publicUrl = "http://157.173.96.252:6034";
      if (htmlContent) {
        htmlContent = htmlContent.replaceAll('src="/static/uploads/', `src="${publicUrl}/static/uploads/`);
        htmlContent = htmlContent.replaceAll('src="http://localhost:6034/static/uploads/', `src="${publicUrl}/static/uploads/`);
        htmlContent = htmlContent.replaceAll('src="http://localhost:3000/static/uploads/', `src="${publicUrl}/static/uploads/`);
        htmlContent = htmlContent.replaceAll('src="/api/images/', `src="${publicUrl}/api/images/`);
        htmlContent = htmlContent.replaceAll('src="http://localhost:6034/api/images/', `src="${publicUrl}/api/images/`);
        htmlContent = htmlContent.replaceAll('src="http://localhost:3000/api/images/', `src="${publicUrl}/api/images/`);
      }
      let emailWrapperHtml = "";
      if (htmlContent) {
        htmlContent = htmlContent.replace(/<img[^>]*Dirham%20Currency%20Symbol%20-%20Black\.svg[^>]*>/gi, '<span style="font-size: 11px; font-weight: bold; margin-right: 2px;">AED</span>');
        const customerName = billDetails?.customerName && billDetails.customerName !== 'N/A' ? billDetails.customerName : 'Customer';
        const activeBranch = localStorage.getItem('active_branch');
        const branchName = (activeBranch && activeBranch !== 'All Branches') ? activeBranch : null;
        const restName = settings?.restaurantName || branchName || localStorage.getItem('active_company') || 'Our Restaurant';
        const finalLogoUrl = logoUrl ? logoUrl.replaceAll('http://localhost:6034', publicUrl).replaceAll('http://localhost:3000', publicUrl).replaceAll('src="/static', `src="${publicUrl}/static`) : '';

        let emailSettingsEmail = settings?.email || 'Email not provided';
        try {
          const esRes = await axios.get('/api/get-email-settings', {
            headers: {
              'X-Company-Name': localStorage.getItem('active_company') || '',
              'X-Branch-Name': localStorage.getItem('active_branch') || ''
            }
          });
          if (esRes.data && esRes.data.settings) {
            emailSettingsEmail = esRes.data.settings.admin_email || esRes.data.settings.sender_email || emailSettingsEmail;
          }
        } catch (esErr) {
          console.error("Failed to fetch email settings", esErr);
        }
        const effectivePrintSettings = printSettings || defaultPrintSettings;
        const phoneBlock = (effectivePrintSettings.headerBlocks || []).find(b =>
          (b.label && b.label.toLowerCase().includes('phone')) ||
          (b.text && (b.text.includes('+') || /\d{9,}/.test(b.text.replace(/\s|-/g, ''))))
        );
        const displayPhone = (phoneBlock && phoneBlock.text) ? phoneBlock.text : (settings?.phone || 'Phone not provided');

        emailWrapperHtml = `
<div style="background-color: #f4f6f8; padding: 40px 20px; font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;">
  <div style="max-width: 600px; margin: 0 auto; background-color: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 15px rgba(0,0,0,0.05);">
    
    <!-- 1. Restaurant Logo Section -->
    <div style="text-align: center; padding: 20px;">
      ${finalLogoUrl ? `<img src="${finalLogoUrl}" alt="Restaurant Logo" style="max-width: 120px; max-height: 80px;" />` : ''}
    </div>

    <!-- 2. Header Section (Purple Background) -->
    <div style="background-color: #604BE8; color: #ffffff; padding: 25px 20px; text-align: center;">
      <h1 style="margin: 0; font-size: 24px; font-weight: 600;">🍽️ Thank You for Your Order!</h1>
    </div>

    <!-- 3. Personalized Greeting Section -->
    <div style="padding: 30px 30px 15px 30px; font-size: 16px; color: #333333; line-height: 1.6;">
      <p style="margin-top: 0;">Hi ${customerName},</p>
      
      <p><strong>Your order has been confirmed successfully.</strong></p>
      
      <p>Thank you for choosing <strong>${restName}</strong>. Your receipt is provided below for your reference.</p>
      
      <p>We hope you enjoyed your meal and had a great experience with us. If you have any questions about your order or need any assistance, we're always happy to help.</p>
      
      <p>We look forward to serving you again soon.</p>
    </div>

    <!-- 4. The Invoice (Receipt) -->
    <div style="padding: 15px 30px 30px 30px; display: flex; justify-content: center;">
       ${htmlContent}
    </div>

    <!-- 5. Footer Section -->
    <div style="background-color: #f1f5f9; padding: 25px; text-align: left; border-top: 1px solid #e2e8f0; font-size: 15px; color: #475569;">
      <h3 style="margin-top: 0; color: #334155; font-size: 18px;">Need Assistance?</h3>
      <p style="margin: 5px 0;">📞 ${displayPhone}</p>
      <p style="margin: 5px 0;">📧 ${emailSettingsEmail}</p>
      <p style="margin: 5px 0;">🌐 <a href="${window.location.origin}" style="color: #604BE8; text-decoration: none;">${window.location.origin}</a></p>
      
      <p style="margin-top: 20px; font-weight: bold; color: #334155; text-align: center;">Thank you for being our valued customer.<br/>We look forward to serving you again soon! ❤️</p>
    </div>

  </div>
</div>`;
      }

      let pdfBase64 = null;
      try {
        const opt = {
          margin: 10,
          filename: `Receipt_${billDetails?.invoice_no || "N/A"}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, allowTaint: true },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
        };

        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = originalHtmlContent || htmlContent;
        const style = document.createElement('style');
        style.innerHTML = `
          body { font-family: 'Dubai Regular', Arial, sans-serif; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #000; padding: 4px; }
        `;
        tempDiv.appendChild(style);
        document.body.appendChild(tempDiv);

        pdfBase64 = await html2pdf().set(opt).from(tempDiv).outputPdf('datauristring');
        document.body.removeChild(tempDiv);
      } catch (pdfError) {
        console.error("Error generating PDF attachment:", pdfError);
      }

      const emailContent = {
        to: emailAddress,
        subject: `Receipt from ${settings?.restaurantName || 'My Restaurant'} - ${billDetails?.invoice_no || "N/A"}`,
        html: emailWrapperHtml,
        pdf_base64: pdfBase64,
        invoice_no: billDetails?.invoice_no || "N/A"
      };

      const response = await axios.post("/api/send-email", emailContent, {
        headers: { "Content-Type": "application/json" },
        timeout: 30000,
      });
      setIsLoading(false);
      if (response.data.success) {
        setWarningMessage("Receipt sent successfully to " + emailAddress);
        setWarningType("success");
      } else {
        setError("Failed to send email: " + response.data.message);
        setWarningMessage("Failed to send email: " + response.data.message);
        setWarningType("warning");
      }
    } catch (err) {
      setIsLoading(false);
      setError("Email sending failed: " + (err.response?.data?.message || err.message));
      setWarningMessage("Email sending failed: " + (err.response?.data?.message || err.message));
      setWarningType("warning");
    }
  };
  // Navigate back to main page
  const handleBack = () => {
    navigate("/frontpage");
  };

  // Edit order (return to frontpage with existing details)
  const handleEdit = () => {
    if (location.state?.frontPageState) {
      navigate("/frontpage", { state: location.state.frontPageState });
    } else if (billDetails) {
      navigate("/frontpage", { state: { existingOrder: billDetails, invoice_no: billDetails.invoice_no } });
    } else {
      navigate("/frontpage");
    }
  };

  // Handle cancel (only close modal, stay on page)
  const handleCancel = () => {
    setShowModal(false);
  };
  // Handle modal close with navigation to frontpage
  const handleModalClose = () => {
    setShowModal(false);
    navigate("/frontpage");
  };
  // Render receipt preview in UI (similar to printsettings) - Hidden in UI, shown only in print
  const renderReceiptPreview = () => {
    if (!billDetails) return null;
    const effectivePrintSettings = printSettings || defaultPrintSettings;
    // Use currentTime for preview
    const formattedDatePreview = getFormattedDate(currentTime, settings.dateFormat);
    const formattedTimePreview = getFormattedTime(currentTime, settings.timeFormat);
    // Render receipt preview
    return (
      <div style={{ display: 'none' }}> {/* Hidden in UI */}
        <h2 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '12px', textAlign: 'center' }}>Receipt Preview</h2>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          {/* Logo on the left */}
          {logoUrl && (
            <div style={{ flex: '0 0 auto' }}>
              <img
                src={logoUrl}
                alt="Logo"
                style={{
                  width: '50px',
                  height: '50px',
                  objectFit: 'contain',
                  borderRadius: '5px'
                }}
                onError={(e) => {
                  e.target.style.display = 'none'; // Hide if error
                }}
              />
            </div>
          )}
          {/* Restaurant details on the right */}
          <div style={{ fontFamily: 'Arial, sans-serif', fontSize: '12px', width: '100%' }}>
            {(effectivePrintSettings.headerBlocks || []).map((b, i) => (
              <div key={i} style={{ textAlign: b.align, minHeight: '14px' }}>{b.text || ''}</div>
            ))}
          </div>
        </div>
        <div style={{ marginTop: '10px', fontSize: '10px' }}>
          <p>Invoice: {billDetails.invoice_no}</p>
          <p>Customer: {billDetails.customerName}</p>
          <p>Date: {formattedDatePreview}</p>
          <p>Time: {formattedTimePreview}</p>
          <p>Total: {formatCurrency(calculateGrandTotal())}</p>
        </div>
        <div style={{ marginTop: '16px' }}>
          {(effectivePrintSettings.footerBlocks || []).map((b, i) => (
            <div key={i} style={{ textAlign: b.align, minHeight: '14px' }}>{b.text || ''}</div>
          ))}
        </div>
      </div>
    );
  };

  // Keyboard Shortcuts Effect
  useEffect(() => {
    if (!isKeyboardShortcutsEnabled) return;

    const handleKeyDown = (e) => {
      // Prevent browser defaults for F keys
      if (e.key === 'F2' || e.key === 'F4' || e.key === 'F8') {
        e.preventDefault();
      }

      if (e.key === 'Escape' && showModal) {
        e.preventDefault();
        handleCancel();
        return;
      }

      if (e.target.tagName === 'INPUT' && e.target.type !== 'number' && e.key !== 'Enter') return;
      if (e.target.tagName === 'TEXTAREA') return;

      if (e.key === 'F4') {
        if (!showModal && billDetails) handlePaymentConfirm();
      } else if (e.key === 'F2') {
        if (!showModal) handleBack();
      } else if (e.key === 'F8') {
        if (showModal) {
          e.preventDefault();
          handleEmail();
        }
      } else if (e.key === 'Enter') {
        if (showModal) {
          e.preventDefault();
          handlePrint();
        } else if (e.target.tagName === 'INPUT' && e.target.type === 'number') {
          e.preventDefault();
          handlePaymentConfirm();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isKeyboardShortcutsEnabled, showModal, billDetails, cashGiven]);

  const handleNumpadClick = (val) => {
    if (val === 'backspace') {
      setCashGiven(prev => prev.slice(0, -1));
      return;
    }
    if (val === '.') {
      if (!cashGiven.includes('.')) {
        setCashGiven(prev => (prev ? prev + '.' : '0.'));
      }
      return;
    }
    // Prevent starting with multiple zeros
    if (val === '0' && cashGiven === '0') return;

    // Replace leading zero if typing a non-zero number
    if (cashGiven === '0' && val !== '.') {
      setCashGiven(val);
      return;
    }

    // Prevent more than 2 decimal places
    if (cashGiven.includes('.')) {
      const decimals = cashGiven.split('.')[1];
      if (decimals && decimals.length >= 2) return;
    }

    setCashGiven(prev => prev + val);
  };

  const setExactCash = (amount) => {
    setCashGiven(amount.toString());
  };

  useEffect(() => {
    if (!billDetails) return;
    if (cashGiven === "" || cashGiven === null || cashGiven === undefined) {
      setChange(0);
    } else {
      const givenAmount = parseFloat(cashGiven) || 0;
      const grandTotal = calculateGrandTotal();
      setChange(Number(givenAmount - grandTotal));
    }
  }, [cashGiven, billDetails]);

  const handleCashInputChange = (e) => {
    const val = e.target.value;
    // Allow only numbers and a single decimal point
    if (/^\d*\.?\d*$/.test(val)) {
      setCashGiven(val);
    }
  };

  return (
    <div className="create-customer-container main-customer-page">
      <style>{styles}</style>

      {/* Header section matching CreateCustomerPage */}
      <div className="customer-header-section">
        <div className="header-left">
          <button className="header-back-btn" onClick={handleBack} disabled={isLoading}>
            <i className="fas fa-arrow-left"></i> Back to Main {isKeyboardShortcutsEnabled && <span className="shortcut-badge-btn" style={{ marginLeft: '8px', fontSize: '0.8em', padding: '2px 6px', background: 'rgba(255,255,255,0.2)', borderRadius: '4px' }}>F2</span>}
          </button>
        </div>
        <div className="header-center">
          <h1>Cash Payment</h1>
        </div>
        <div className="header-right header-actions"></div>
      </div>

      <div className="customer-scroll-body">
        <div className="main-content-card">
          {/* Warning message display */}
          {warningMessage && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10050,
              animation: 'fadeIn 0.2s ease-out'
            }}>
              <div style={{
                backgroundColor: '#ffffff',
                borderRadius: '16px',
                padding: '30px',
                width: '420px',
                maxWidth: '90%',
                textAlign: 'center',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
                border: '1px solid #e2e8f0',
              }}>
                <div style={{
                  fontSize: '48px',
                  marginBottom: '16px',
                  color: warningType === 'success' ? '#10b981' : '#f59e0b'
                }}>
                  {warningType === 'success' ? '✓' : '⚠'}
                </div>
                <h4 style={{
                  margin: '0 0 12px 0',
                  color: '#1e293b',
                  fontWeight: '700',
                  fontSize: '18px'
                }}>
                  {warningType === 'success' ? 'Success' : 'Notification'}
                </h4>
                <p style={{
                  margin: '0 0 24px 0',
                  color: '#64748b',
                  fontSize: '15px',
                  lineHeight: '1.5'
                }}>
                  {warningMessage}
                </p>
                <button
                  onClick={handleWarningOk}
                  className="btn btn-primary"
                  style={{
                    padding: '12px 30px',
                    background: warningType === 'success' ? '#10b981' : '#604BE8',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '10px',
                    fontWeight: '600',
                    fontSize: '14px',
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  }}
                >
                  OK
                </button>
              </div>
            </div>
          )}

          {/* Error message display */}
          {error && <div className="cash-error">{error}</div>}

          <div className="payment-sections-wrapper" style={{
            marginTop: '0px',
            width: '100%',
            minHeight: 'calc(100vh - 150px)',
            backgroundColor: '#f4f7f9',
            padding: '24px'
          }}>
            {billDetails ? (
              <div style={{ display: 'grid', gridTemplateColumns: '60% 38%', gap: '2%', width: '100%', maxWidth: '1400px', margin: '0 auto' }}>

                {/* Left Column: Order Summary */}
                <div style={{ background: 'white', borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', padding: '24px', display: 'flex', flexDirection: 'column' }}>

                  {/* Header Row */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
                    <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                      <div style={{ width: '48px', height: '48px', background: '#f3f4f6', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#1e293b', fontSize: '20px' }}>
                        <i className="fas fa-receipt"></i>
                      </div>
                      <div>
                        <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#111827', margin: '0 0 4px 0' }}>Items ordered</h2>
                        <p style={{ margin: 0, color: '#6b7280', fontSize: '14px' }}>Order - {billDetails.tableNumber ? 'Dine-In' : 'Takeaway'}</p>
                      </div>
                    </div>
                    <div style={{ background: '#f3f4f6', padding: '8px 16px', borderRadius: '20px', color: '#4b5563', fontSize: '14px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <i className="far fa-user"></i> {billDetails.customerGroup || billDetails.customer_group || 'Walk-in customer'}
                    </div>
                  </div>

                  {/* Customer Details */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', paddingBottom: '20px', borderBottom: '1px solid #e5e7eb', marginBottom: '16px' }}>
                    <div>
                      <p style={{ margin: '0 0 4px 0', fontSize: '12px', fontWeight: '600', color: '#9ca3af', letterSpacing: '0.5px' }}>CUSTOMER</p>
                      <p style={{ margin: 0, fontSize: '15px', color: '#111827', fontWeight: '500' }}>{billDetails.customerName || 'N/A'}</p>
                    </div>
                    <div>
                      <p style={{ margin: '0 0 4px 0', fontSize: '12px', fontWeight: '600', color: '#9ca3af', letterSpacing: '0.5px' }}>PHONE</p>
                      <p style={{ margin: 0, fontSize: '15px', color: '#111827', fontWeight: '500' }}>{billDetails.phoneNumber || 'N/A'}</p>
                    </div>
                    <div>
                      <p style={{ margin: '0 0 4px 0', fontSize: '12px', fontWeight: '600', color: '#9ca3af', letterSpacing: '0.5px' }}>EMAIL</p>
                      <p style={{ margin: 0, fontSize: '15px', color: '#111827', fontWeight: '500' }}>{billDetails.email || 'N/A'}</p>
                    </div>
                  </div>

                  {/* Item List Header */}
                  <div style={{ display: 'flex', borderBottom: '1px solid #f3f4f6', paddingBottom: '12px', marginBottom: '16px' }}>
                    <div style={{ flex: 1, fontSize: '12px', fontWeight: '600', color: '#9ca3af', letterSpacing: '0.5px' }}>ITEM</div>
                    <div style={{ width: '80px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#9ca3af', letterSpacing: '0.5px' }}>QTY</div>
                    <div style={{ width: '100px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#9ca3af', letterSpacing: '0.5px' }}>PRICE</div>
                  </div>

                  {/* Item List */}
                  <div style={{ flex: 1, overflowY: 'auto', paddingRight: '8px', minHeight: '200px' }}>
                    {billDetails.items.map((item, index) => {
                      const { basePrice, icePrice, spicyPrice } = calculateItemPrices(item);
                      return (
                        <React.Fragment key={index}>
                          {/* Main Item */}
                          <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '16px' }}>
                            <div style={{ flex: 1, fontSize: '15px', fontWeight: '700', color: '#1f2937' }}>
                              {getItemDisplayName(item)}
                            </div>
                            <div style={{ width: '80px', textAlign: 'right', fontSize: '15px', fontWeight: '600', color: '#374151' }}>
                              {item.quantity}
                            </div>
                            <div style={{ width: '100px', textAlign: 'right', fontSize: '15px', fontWeight: '600', color: '#374151' }}>
                              {formatCurrencyReact(basePrice)}
                            </div>
                          </div>

                          {/* Sub-items (Combos, Toppings, Addons) */}
                          <div style={{ paddingLeft: '24px', marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {item.isSpicy && spicyPrice > 0 && (
                              <div style={{ display: 'flex', color: '#6b7280', fontSize: '14px' }}>
                                <div style={{ flex: 1 }}>+ Spicy</div>
                                <div style={{ width: '80px', textAlign: 'right' }}>{item.quantity}</div>
                                <div style={{ width: '100px', textAlign: 'right' }}>{formatCurrencyReact(spicyPrice)}</div>
                              </div>
                            )}
                            {item.customVariantsDetails && Object.keys(item.customVariantsDetails).length > 0 && Object.entries(item.customVariantsDetails).map(([variantName, variant], idx) => (
                              <div key={`cv-${idx}`} style={{ display: 'flex', color: '#6b7280', fontSize: '14px' }}>
                                <div style={{ flex: 1 }}>+ {variant.heading || variantName}: {variant.name}</div>
                                <div style={{ width: '80px', textAlign: 'right' }}>{(item.customVariantsQuantities?.[variantName] || 1) * item.quantity}</div>
                                <div style={{ width: '100px', textAlign: 'right' }}>{formatCurrencyReact(variant.price)}</div>
                              </div>
                            ))}
                            {item.itemToppings && Object.keys(item.itemToppings).length > 0 && Object.entries(item.itemToppings).map(([name, top], idx) => (
                              <div key={`top-${idx}`} style={{ display: 'flex', color: '#6b7280', fontSize: '14px' }}>
                                <div style={{ flex: 1 }}>+ {top.name} (Topping)</div>
                                <div style={{ width: '80px', textAlign: 'right' }}>{item.quantity * (top.qty || 1)}</div>
                                <div style={{ width: '100px', textAlign: 'right' }}>{formatCurrencyReact(top.price)}</div>
                              </div>
                            ))}
                            {item.comboItems && item.comboItems.length > 0 && item.comboItems.map((comboItem, idx) => (
                              <div key={`offer-cmb-${idx}`} style={{ display: 'flex', color: '#6b7280', fontSize: '14px' }}>
                                <div style={{ flex: 1 }}>+ {comboItem.name}</div>
                                <div style={{ width: '80px', textAlign: 'right' }}></div>
                                <div style={{ width: '100px', textAlign: 'right' }}></div>
                              </div>
                            ))}
                          </div>

                          {/* Addons rendered like main items */}
                          {item.addons && item.addons.map((addon, idx) => addon.addon_quantity > 0 && (
                            <React.Fragment key={`add-${idx}`}>
                              <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '12px' }}>
                                <div style={{ flex: 1, fontSize: '15px', fontWeight: '700', color: '#1f2937' }}>
                                  {addon.addon_name || addon.name1}
                                </div>
                                <div style={{ width: '80px', textAlign: 'right', fontSize: '15px', fontWeight: '600', color: '#374151' }}>
                                  {addon.addon_quantity}
                                </div>
                                <div style={{ width: '100px', textAlign: 'right', fontSize: '15px', fontWeight: '600', color: '#374151' }}>
                                  {formatCurrencyReact(addon.addon_price)}
                                </div>
                              </div>

                              {/* Addon Sub-items */}
                              <div style={{ paddingLeft: '24px', marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                {addon.custom_variants && Object.entries(addon.custom_variants).map(([cvName, cv], cvIdx) => (
                                  <div key={`add-cv-${idx}-${cvIdx}`} style={{ display: 'flex', color: '#6b7280', fontSize: '14px' }}>
                                    <div style={{ flex: 1 }}>+ {cv.heading || cvName}: {cv.name}</div>
                                    <div style={{ width: '80px', textAlign: 'right' }}>{addon.addon_quantity}</div>
                                    <div style={{ width: '100px', textAlign: 'right' }}>{formatCurrencyReact(cv.price)}</div>
                                  </div>
                                ))}
                                {addon.addon_toppings && Object.entries(addon.addon_toppings).map(([topName, top], topIdx) => (
                                  <div key={`add-top-${idx}-${topIdx}`} style={{ display: 'flex', color: '#6b7280', fontSize: '14px' }}>
                                    <div style={{ flex: 1 }}>+ {top.name} (Topping)</div>
                                    <div style={{ width: '80px', textAlign: 'right' }}>{addon.addon_quantity * (top.qty || 1)}</div>
                                    <div style={{ width: '100px', textAlign: 'right' }}>{formatCurrencyReact(top.price)}</div>
                                  </div>
                                ))}
                              </div>
                            </React.Fragment>
                          ))}

                          {/* Combos rendered like main items */}
                          {item.combos && item.combos.map((combo, idx) => combo.combo_quantity > 0 && (
                            <React.Fragment key={`cmb-${idx}`}>
                              <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '12px' }}>
                                <div style={{ flex: 1, fontSize: '15px', fontWeight: '700', color: '#1f2937' }}>
                                  {combo.name1}
                                </div>
                                <div style={{ width: '80px', textAlign: 'right', fontSize: '15px', fontWeight: '600', color: '#374151' }}>
                                  {combo.combo_quantity}
                                </div>
                                <div style={{ width: '100px', textAlign: 'right', fontSize: '15px', fontWeight: '600', color: '#374151' }}>
                                  {formatCurrencyReact(combo.combo_price)}
                                </div>
                              </div>

                              {/* Combo Sub-items */}
                              <div style={{ paddingLeft: '24px', marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                {combo.custom_variants && Object.entries(combo.custom_variants).map(([cvName, cv], cvIdx) => (
                                  <div key={`cmb-cv-${idx}-${cvIdx}`} style={{ display: 'flex', color: '#6b7280', fontSize: '14px' }}>
                                    <div style={{ flex: 1 }}>+ {cv.heading || cvName}: {cv.name}</div>
                                    <div style={{ width: '80px', textAlign: 'right' }}>{combo.combo_quantity}</div>
                                    <div style={{ width: '100px', textAlign: 'right' }}>{formatCurrencyReact(cv.price)}</div>
                                  </div>
                                ))}
                                {combo.combo_toppings && Object.entries(combo.combo_toppings).map(([topName, top], topIdx) => (
                                  <div key={`cmb-top-${idx}-${topIdx}`} style={{ display: 'flex', color: '#6b7280', fontSize: '14px' }}>
                                    <div style={{ flex: 1 }}>+ {top.name} (Topping)</div>
                                    <div style={{ width: '80px', textAlign: 'right' }}>{combo.combo_quantity * (top.qty || 1)}</div>
                                    <div style={{ width: '100px', textAlign: 'right' }}>{formatCurrencyReact(top.price)}</div>
                                  </div>
                                ))}
                              </div>
                            </React.Fragment>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </div>

                  {/* Totals Section */}
                  <div style={{ background: '#f9fafb', borderRadius: '16px', padding: '24px', marginTop: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '14px', color: '#6b7280' }}>
                      <span>Total quantity</span>
                      <span style={{ fontWeight: '600', color: '#111827' }}>{billDetails.items.reduce((sum, item) => sum + item.quantity, 0)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '14px', color: '#6b7280' }}>
                      <span>Subtotal</span>
                      <span style={{ fontWeight: '700', color: '#111827' }}>{formatCurrencyReact(calculateSubtotal())}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: parseFloat(pointsToRedeem) > 0 ? '12px' : '24px', fontSize: '14px', color: '#6b7280' }}>
                      <span>Total VAT</span>
                      <span style={{ fontWeight: '700', color: '#111827' }}>{formatCurrencyReact(calculateVAT())}</span>
                    </div>

                    {parseFloat(pointsToRedeem) > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', padding: '10px 14px', background: '#dcfce7', borderRadius: '10px', border: '1px solid #bbf7d0' }}>
                        <span style={{ fontSize: '14px', fontWeight: '600', color: '#15803d', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <i className="fa-solid fa-star" style={{ color: '#16a34a', fontSize: '12px' }}></i>
                          Points Redeemed ({parseFloat(pointsToRedeem)} pts)
                        </span>
                        <span style={{ fontSize: '15px', fontWeight: '800', color: '#15803d' }}>
                          -{formatCurrencyReact(calculatePointsDiscount())}
                        </span>
                      </div>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
                      <span style={{ fontSize: '16px', fontWeight: '800', color: '#111827' }}>Grand total</span>
                      <span style={{ fontSize: '24px', fontWeight: '800', color: '#5b57f4' }}>{formatCurrencyReact(calculateGrandTotal())}</span>
                    </div>
                  </div>

                  {/* Reward Points Redemption Section */}
                  {customerPoints > 0 && (
                    <div style={{ background: 'linear-gradient(135deg, #f0f4ff 0%, #faf5ff 100%)', borderRadius: '16px', padding: '20px', border: '1px solid #c7d2fe', marginTop: '20px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <span style={{ fontSize: '14px', fontWeight: '700', color: '#3730a3', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <i className="fa-solid fa-gift" style={{ color: '#5b57f4' }}></i>
                          Reward Points
                        </span>
                        <span style={{ fontSize: '13px', fontWeight: '700', color: '#6366f1', background: '#e0e7ff', padding: '4px 12px', borderRadius: '20px' }}>
                          {customerPoints} pts available
                        </span>
                      </div>

                      <div style={{ marginBottom: '10px', fontSize: '12px', color: '#64748b', fontWeight: '500' }}>
                        1 pt = {rewardSettings.conversion_rate} {rewardSettings.currency} &nbsp;·&nbsp;
                        Max usable: <strong>{(() => {
                          let limit = Math.min(customerPoints, Math.ceil((billDetails?.totalAmount || 0) / parseFloat(rewardSettings.conversion_rate || 1)));
                          if (rewardSettings?.max_redemption_limit > 0) limit = Math.min(limit, rewardSettings.max_redemption_limit);
                          return limit;
                        })()} pts</strong> to cover this bill
                      </div>

                      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <input
                          type="number"
                          min="0"
                          max={(() => {
                            let limit = Math.min(customerPoints, Math.ceil((billDetails?.totalAmount || 0) / parseFloat(rewardSettings.conversion_rate || 1)));
                            if (rewardSettings?.max_redemption_limit > 0) limit = Math.min(limit, rewardSettings.max_redemption_limit);
                            return limit;
                          })()}
                          placeholder="Points to redeem"
                          value={pointsToRedeem}
                          onChange={(e) => {
                            let maxAllowed = Math.min(customerPoints, Math.ceil((billDetails?.totalAmount || 0) / parseFloat(rewardSettings.conversion_rate || 1)));
                            if (rewardSettings?.max_redemption_limit > 0) maxAllowed = Math.min(maxAllowed, rewardSettings.max_redemption_limit);
                            
                            let val = parseFloat(e.target.value);
                            if (val > maxAllowed) val = maxAllowed;
                            if (val < 0) val = 0;
                            setPointsToRedeem(val || "");
                          }}
                          style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid #c7d2fe', fontSize: '14px', background: '#fff' }}
                        />
                        <button
                          onClick={() => {
                            let maxAllowed = Math.min(customerPoints, Math.ceil((billDetails?.totalAmount || 0) / parseFloat(rewardSettings.conversion_rate || 1)));
                            if (rewardSettings?.max_redemption_limit > 0) maxAllowed = Math.min(maxAllowed, rewardSettings.max_redemption_limit);
                            setPointsToRedeem(maxAllowed);
                          }}
                          style={{ padding: '10px 16px', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '600', fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                        >
                          Max
                        </button>
                      </div>

                      {parseFloat(pointsToRedeem) > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', padding: '10px 14px', background: '#dcfce7', borderRadius: '10px', border: '1px solid #bbf7d0' }}>
                          <span style={{ fontSize: '13px', fontWeight: '600', color: '#166534' }}>✅ Discount Applied</span>
                          <span style={{ fontSize: '14px', fontWeight: '800', color: '#15803d' }}>
                            -{formatCurrencyReact(calculatePointsDiscount())}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Right Column: Payment Panel */}
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <div style={{ background: 'white', borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', padding: '30px', flex: 1, display: 'flex', flexDirection: 'column' }}>

                    {rewardPointsData && rewardPointsData.available_points > 0 && (
                      <div style={{ background: '#e0e7ff', borderRadius: '12px', padding: '16px', marginBottom: '12px', border: '1px solid #c7d2fe' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <p style={{ margin: 0, fontSize: '12px', fontWeight: '700', color: '#4f46e5', textTransform: 'uppercase', letterSpacing: '0.5px' }}>AVAILABLE REWARD POINTS</p>
                            <p style={{ margin: '4px 0 0 0', fontSize: '24px', fontWeight: '800', color: '#3730a3' }}>{rewardPointsData.available_points}</p>
                          </div>
                          <i className="fas fa-gift" style={{ fontSize: '24px', color: '#6366f1' }}></i>
                        </div>
                      </div>
                    )}

                    {pointsToEarn > 0 && (
                      <div style={{ background: '#f0fdf4', borderRadius: '12px', padding: '16px', marginBottom: '20px', border: '1px solid #bbf7d0' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <p style={{ margin: 0, fontSize: '12px', fontWeight: '700', color: '#16a34a', textTransform: 'uppercase', letterSpacing: '0.5px' }}>POINTS TO EARN</p>
                            <p style={{ margin: '4px 0 0 0', fontSize: '24px', fontWeight: '800', color: '#166534' }}>+{pointsToEarn}</p>
                          </div>
                          <i className="fas fa-star" style={{ fontSize: '24px', color: '#22c55e' }}></i>
                        </div>
                      </div>
                    )}

                    <p style={{ margin: '0 0 12px 0', fontSize: '12px', fontWeight: '600', color: '#9ca3af', letterSpacing: '1px', textTransform: 'uppercase' }}>CASH GIVEN</p>

                    {/* Cash Given Display */}
                    <div style={{ background: '#f8fafc', borderRadius: '16px', padding: '20px 24px', display: 'flex', alignItems: 'center', marginBottom: '20px', border: '1px solid #e2e8f0', transition: 'border-color 0.2s' }}
                      onFocus={(e) => e.currentTarget.style.borderColor = '#5b57f4'}
                      onBlur={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}>
                      <span style={{ fontSize: '20px', color: '#64748b', marginRight: '8px' }}>₹</span>
                      <input
                        type="text"
                        value={cashGiven}
                        onChange={handleCashInputChange}
                        placeholder="0"
                        style={{ background: 'transparent', border: 'none', fontSize: '32px', fontWeight: '800', color: '#0f172a', width: '100%', outline: 'none' }}
                      />
                    </div>

                    {/* Quick Amounts */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '20px' }}>
                      {[50, 100, 200, 500].map(amt => (
                        <button
                          key={amt}
                          onClick={() => setExactCash(amt)}
                          style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '12px', fontSize: '14px', fontWeight: '600', color: '#1e293b', cursor: 'pointer', transition: 'all 0.2s' }}
                          onMouseOver={(e) => { e.currentTarget.style.borderColor = '#5b57f4'; e.currentTarget.style.color = '#5b57f4' }}
                          onMouseOut={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.color = '#1e293b' }}
                        >
                          {amt}
                        </button>
                      ))}
                    </div>

                    {/* Numpad */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '32px' }}>
                      {['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0', 'backspace'].map((key) => (
                        <button
                          key={key}
                          onClick={() => handleNumpadClick(key)}
                          style={{
                            background: 'white',
                            border: '1px solid #e2e8f0',
                            borderRadius: '12px',
                            padding: '16px',
                            fontSize: '20px',
                            fontWeight: '600',
                            color: '#1e293b',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '64px',
                            transition: 'all 0.1s'
                          }}
                          onMouseDown={(e) => e.currentTarget.style.background = '#f1f5f9'}
                          onMouseUp={(e) => e.currentTarget.style.background = 'white'}
                        >
                          {key === 'backspace' ? <i className="fas fa-backspace" style={{ color: '#64748b' }}></i> : key}
                        </button>
                      ))}
                    </div>

                    {/* Change Return */}
                    <div style={{ background: '#f8fafc', borderRadius: '16px', padding: '20px 24px', marginBottom: 'auto' }}>
                      <p style={{ margin: '0 0 8px 0', fontSize: '12px', fontWeight: '600', color: '#9ca3af', letterSpacing: '1px', textTransform: 'uppercase' }}>CHANGE TO RETURN</p>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ fontSize: '32px', fontWeight: '800', color: change < 0 ? '#ef4444' : '#64748b' }}>{formatCurrencyReact(change)}</span>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div style={{ marginTop: '24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <button
                        onClick={handlePaymentConfirm}
                        disabled={isLoading}
                        style={{ background: '#a1a0f8', color: 'white', border: 'none', borderRadius: '16px', padding: '18px', fontSize: '16px', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer', transition: 'background 0.2s' }}
                        onMouseOver={(e) => !isLoading && (e.currentTarget.style.background = '#8c8bf4')}
                        onMouseOut={(e) => !isLoading && (e.currentTarget.style.background = '#a1a0f8')}
                      >
                        <i className="far fa-check-circle" style={{ fontSize: '18px' }}></i>
                        Confirm payment
                        {isKeyboardShortcutsEnabled && <span style={{ marginLeft: '8px', background: 'rgba(255,255,255,0.2)', padding: '2px 8px', borderRadius: '6px', fontSize: '12px' }}>F4</span>}
                      </button>

                      <button
                        onClick={handleEdit}
                        disabled={isLoading}
                        style={{ background: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '18px', fontSize: '16px', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer', transition: 'background 0.2s' }}
                        onMouseOver={(e) => !isLoading && (e.currentTarget.style.background = '#f1f5f9')}
                        onMouseOut={(e) => !isLoading && (e.currentTarget.style.background = '#f8fafc')}
                      >
                        <i className="fas fa-pen" style={{ fontSize: '14px' }}></i>
                        Edit order
                      </button>
                    </div>

                    {/* Hidden receipt preview for printing */}
                    <div style={{ display: 'none' }}>
                      {renderReceiptPreview()}
                    </div>

                  </div>
                </div>
              </div>
            ) : (
              <div className="cash-empty" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
                <i className="fas fa-file-invoice-dollar" style={{ fontSize: '64px', color: '#cbd5e1', marginBottom: '24px' }}></i>
                <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#1e293b', marginBottom: '16px' }}>No active bill found</h2>
                <button
                  onClick={handleBack}
                  style={{ background: '#604be8', color: 'white', padding: '12px 24px', borderRadius: '8px', border: 'none', fontSize: '16px', fontWeight: '600', cursor: 'pointer' }}
                >
                  Return to Main Menu
                </button>
              </div>
            )}
          </div>
          {/* Modal for bill details and actions */}
          <Modal show={showModal} onHide={handleCancel} size="lg" centered scrollable contentClassName="border-0 shadow-lg overflow-hidden" style={{ borderRadius: '24px' }}>
            <Modal.Body style={{ padding: '0', background: '#f8fafc', maxHeight: '75vh', overflowY: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)', borderBottom: '1px solid #e2e8f0', padding: '24px 32px' }}>
                <div style={{ fontWeight: '800', color: '#1e293b', fontSize: '24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ background: '#5b57f4', color: 'white', width: '40px', height: '40px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(91, 87, 244, 0.3)' }}>
                    <span style={{ fontSize: '24px', fontWeight: 'bold' }}>✓</span>
                  </div>
                  Payment Successful
                </div>
                <button onClick={handleCancel} style={{ background: 'transparent', border: 'none', fontSize: '28px', cursor: 'pointer', color: '#94a3b8', lineHeight: 1 }}>&times;</button>
              </div>
              {/* Top action row */}
              <div style={{ padding: '20px 32px', background: 'white', borderBottom: '1px solid #e2e8f0' }}>
                <label style={{ fontSize: '13px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px', display: 'block' }}>Email Receipt To</label>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <div style={{ position: 'relative', flex: 1 }}>
                    <i className="far fa-envelope" style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}></i>
                    <input
                      type="email"
                      style={{ width: '100%', padding: '12px 16px 12px 48px', borderRadius: '12px', border: '1px solid #e2e8f0', background: '#f8fafc', fontSize: '15px', color: '#1e293b', outline: 'none', transition: 'border-color 0.2s, background 0.2s' }}
                      onFocus={(e) => { e.currentTarget.style.borderColor = '#5b57f4'; e.currentTarget.style.background = '#ffffff'; }}
                      onBlur={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.background = '#f8fafc'; }}
                      value={emailAddress}
                      onChange={(e) => setEmailAddress(e.target.value)}
                      placeholder="customer@example.com"
                      disabled={isLoading}
                    />
                  </div>
                  <Button
                    onClick={handleEmail}
                    disabled={isLoading}
                    style={{ background: '#5b57f4', color: 'white', border: 'none', borderRadius: '12px', padding: '0 24px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px', transition: 'background 0.2s' }}
                    onMouseOver={(e) => e.currentTarget.style.background = '#4a47d6'}
                    onMouseOut={(e) => e.currentTarget.style.background = '#5b57f4'}
                  >
                    {isLoading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-paper-plane"></i>}
                    <span>Send</span>
                    {isKeyboardShortcutsEnabled && <span style={{ background: 'rgba(255,255,255,0.2)', padding: '2px 6px', borderRadius: '6px', fontSize: '11px', marginLeft: '4px' }}>F8</span>}
                  </Button>
                </div>
              </div>

              {billDetails && (
                <div style={{ padding: '32px' }}>
                  <div style={{ background: 'white', borderRadius: '20px', padding: '32px', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', border: '1px solid #f1f5f9' }}>

                    {/* Bill Header Info Grid */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '24px', marginBottom: '32px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <span style={{ fontSize: '12px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Invoice No</span>
                        <span style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>{billDetails.invoice_no}</span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <span style={{ fontSize: '12px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Date & Time</span>
                        <span style={{ fontSize: '16px', fontWeight: '600', color: '#334155' }}>
                          {getFormattedDate(new Date(), settings.dateFormat)} • {getFormattedTime(new Date(), settings.timeFormat)}
                        </span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <span style={{ fontSize: '12px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Customer</span>
                        <span style={{ fontSize: '16px', fontWeight: '600', color: '#334155' }}>{billDetails.customerName}</span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <span style={{ fontSize: '12px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Payment Mode</span>
                        <span style={{ fontSize: '14px', fontWeight: '700', color: '#059669', background: '#d1fae5', padding: '4px 12px', borderRadius: '20px', alignSelf: 'flex-start', display: 'inline-block' }}>
                          {billDetails.payments?.[0]?.mode_of_payment || "CASH"}
                        </span>
                      </div>
                    </div>

                    {/* Delivery / Table Info */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', paddingBottom: '24px', borderBottom: '2px dashed #e2e8f0', marginBottom: '24px' }}>
                      {billDetails.phoneNumber && billDetails.phoneNumber !== "N/A" && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#475569', fontSize: '14px' }}>
                          <i className="fas fa-phone" style={{ color: '#94a3b8' }}></i> {billDetails.phoneNumber}
                        </div>
                      )}
                      {billDetails.tableNumber && billDetails.tableNumber !== "N/A" && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#475569', fontSize: '14px', fontWeight: '600' }}>
                          <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#f59e0b' }}></div>
                          Table: {billDetails.tableNumber}
                        </div>
                      )}
                      {hasDeliveryAddress && formattedDeliveryAddress && (
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', color: '#475569', fontSize: '14px', flex: '1 1 100%' }}>
                          <i className="fas fa-map-marker-alt" style={{ color: '#ef4444', marginTop: '4px' }}></i>
                          <span style={{ lineHeight: '1.5' }}>{formattedDeliveryAddress}</span>
                        </div>
                      )}
                    </div>

                    {/* Items List */}
                    <div>
                      <h4 style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <i className="fas fa-shopping-bag" style={{ color: '#64748b' }}></i> Order Items
                      </h4>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        {billDetails.items.map((item, index) => {
                          const { basePrice, icePrice, spicyPrice } = calculateItemPrices(item);
                          return (
                            <div key={index} style={{ display: 'flex', flexDirection: 'column', paddingBottom: '16px', borderBottom: index === billDetails.items.length - 1 ? 'none' : '1px solid #f1f5f9' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <div style={{ flex: 1 }}>
                                  <span style={{ fontSize: '15px', fontWeight: '700', color: '#1e293b', display: 'block' }}>{getItemDisplayName(item)}</span>
                                  {item.originalBasePrice && (
                                    <span style={{ fontSize: '13px', color: '#94a3b8', textDecoration: 'line-through', marginRight: '8px' }}>{formatCurrency(item.originalBasePrice)}</span>
                                  )}
                                </div>
                                <div style={{ width: '40px', textAlign: 'center', fontSize: '14px', fontWeight: '600', color: '#64748b', background: '#f1f5f9', borderRadius: '6px', padding: '2px 0' }}>
                                  x{item.quantity}
                                </div>
                                <div style={{ width: '90px', textAlign: 'right', fontSize: '15px', fontWeight: '700', color: '#1e293b' }}>
                                  {formatCurrency(basePrice * item.quantity)}
                                </div>
                              </div>

                              {/* Addons/Extras */}
                              <div style={{ paddingLeft: '16px', marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                {item.comboItems && item.comboItems.length > 0 && item.comboItems.map((comboItem, cIndex) => (
                                  <div key={`combo-${cIndex}`} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#64748b' }}>
                                    <span>+ {comboItem.name}</span>
                                  </div>
                                ))}
                                {item.icePreference === "with_ice" && icePrice > 0 && (
                                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#64748b' }}>
                                    <span>+ Ice</span>
                                    <span>{formatCurrency(icePrice * item.quantity)}</span>
                                  </div>
                                )}
                                {item.isSpicy && spicyPrice > 0 && (
                                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#64748b' }}>
                                    <span>+ Spicy</span>
                                    <span>{formatCurrency(spicyPrice * item.quantity)}</span>
                                  </div>
                                )}
                                {item.customVariantsDetails && Object.keys(item.customVariantsDetails).length > 0 && Object.entries(item.customVariantsDetails).map(([variantName, variant], idx) => (
                                  <div key={`custom-${idx}`} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#64748b' }}>
                                    <span>+ {variant.heading}: {variant.name} x{(item.customVariantsQuantities?.[variantName] || 1) * item.quantity}</span>
                                    <span>{formatCurrency(variant.price * (item.customVariantsQuantities?.[variantName] || 1) * item.quantity)}</span>
                                  </div>
                                ))}
                                {item.itemToppings && Object.keys(item.itemToppings).length > 0 && Object.entries(item.itemToppings).map(([name, top], idx) => (
                                  <div key={`top-${idx}`} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#64748b' }}>
                                    <span>+ {top.name} (Topping) x{(top.qty || 1) * item.quantity}</span>
                                    <span>{formatCurrency(top.price * (top.qty || 1) * item.quantity)}</span>
                                  </div>
                                ))}
                                {item.addons && item.addons.map((addon, idx) => addon.addon_quantity > 0 && (
                                  <React.Fragment key={`add-${idx}`}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#000000', fontWeight: 'bold', marginTop: '6px' }}>
                                      <span>{addon.addon_name || addon.name1}{addon.size ? ` (${addon.size})` : ""} x{addon.addon_quantity}</span>
                                      <span>{formatCurrency(addon.addon_price * addon.addon_quantity)}</span>
                                    </div>
                                    {addon.isSpicy && addon.spicyPrice > 0 && (
                                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#64748b', paddingLeft: '12px' }}>
                                        <span>+ Spicy</span>
                                        <span>{formatCurrency(addon.spicyPrice * addon.addon_quantity)}</span>
                                      </div>
                                    )}
                                    {addon.custom_variants && Object.entries(addon.custom_variants).map(([cvName, cv], cvIdx) => (
                                      <div key={`add-cv-${cvIdx}`} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#64748b', paddingLeft: '12px' }}>
                                        <span>+ {cv.heading || cvName}: {cv.name} x{addon.addon_quantity}</span>
                                        <span>{formatCurrency(cv.price * addon.addon_quantity)}</span>
                                      </div>
                                    ))}
                                    {addon.addon_toppings && Object.entries(addon.addon_toppings).map(([topName, top], topIdx) => (
                                      <div key={`add-top-${topIdx}`} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#64748b', paddingLeft: '12px' }}>
                                        <span>+ {top.name} (Topping) x{addon.addon_quantity * (top.qty || 1)}</span>
                                        <span>{formatCurrency(top.price * addon.addon_quantity * (top.qty || 1))}</span>
                                      </div>
                                    ))}
                                  </React.Fragment>
                                ))}
                                {item.combos && item.combos.map((combo, idx) => combo.combo_quantity > 0 && (
                                  <React.Fragment key={`cmb-${idx}`}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#000000', fontWeight: 'bold', marginTop: '6px' }}>
                                      <span>{combo.name1}{combo.size ? ` (${combo.size})` : ""} x{combo.combo_quantity}</span>
                                      <span>{formatCurrency(combo.combo_price * combo.combo_quantity)}</span>
                                    </div>
                                    {combo.isSpicy && combo.spicyPrice > 0 && (
                                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#64748b', paddingLeft: '12px' }}>
                                        <span>+ Spicy</span>
                                        <span>{formatCurrency(combo.spicyPrice * combo.combo_quantity)}</span>
                                      </div>
                                    )}
                                    {combo.custom_variants && Object.entries(combo.custom_variants).map(([cvName, cv], cvIdx) => (
                                      <div key={`cmb-cv-${cvIdx}`} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#64748b', paddingLeft: '12px' }}>
                                        <span>+ {cv.heading || cvName}: {cv.name} x{combo.combo_quantity}</span>
                                        <span>{formatCurrency(cv.price * combo.combo_quantity)}</span>
                                      </div>
                                    ))}
                                    {combo.combo_toppings && Object.entries(combo.combo_toppings).map(([topName, top], topIdx) => (
                                      <div key={`cmb-top-${topIdx}`} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#64748b', paddingLeft: '12px' }}>
                                        <span>+ {top.name} (Topping) x{combo.combo_quantity * (top.qty || 1)}</span>
                                        <span>{formatCurrency(top.price * combo.combo_quantity * (top.qty || 1))}</span>
                                      </div>
                                    ))}
                                  </React.Fragment>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Totals Box */}
                    <div style={{ marginTop: '24px', background: '#f8fafc', borderRadius: '16px', padding: '24px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '14px', color: '#475569' }}>
                        <span>Subtotal</span>
                        <span style={{ fontWeight: '600', color: '#1e293b' }}>{formatCurrency(calculateSubtotal())}</span>
                      </div>
                      {Object.entries(getVatByRate()).map(([rate, amt]) => (
                        <div key={rate} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '14px', color: '#475569' }}>
                          <span>VAT ({rate}%)</span>
                          <span style={{ fontWeight: '600', color: '#1e293b' }}>{formatCurrency(amt)}</span>
                        </div>
                      ))}
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: parseFloat(pointsToRedeem) > 0 ? '12px' : '20px', fontSize: '14px', color: '#475569' }}>
                        <span>Total VAT</span>
                        <span style={{ fontWeight: '600', color: '#1e293b' }}>{formatCurrency(calculateVAT())}</span>
                      </div>

                      {parseFloat(pointsToRedeem) > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', padding: '12px 16px', background: 'linear-gradient(135deg, #dcfce7, #d1fae5)', borderRadius: '12px', border: '1px solid #86efac' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <span style={{ fontSize: '14px', fontWeight: '700', color: '#15803d', display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <i className="fa-solid fa-star" style={{ color: '#16a34a', fontSize: '12px' }}></i>
                              Points Redeemed
                            </span>
                            <span style={{ fontSize: '12px', color: '#166534', fontWeight: '500' }}>{parseFloat(pointsToRedeem)} pts used as discount</span>
                          </div>
                          <span style={{ fontSize: '16px', fontWeight: '800', color: '#15803d' }}>-{formatCurrency(calculatePointsDiscount())}</span>
                        </div>
                      )}

                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '20px', borderTop: '1px solid #e2e8f0' }}>
                        <span style={{ fontSize: '18px', fontWeight: '800', color: '#0f172a' }}>Grand Total</span>
                        <span style={{ fontSize: '28px', fontWeight: '800', color: '#5b57f4' }}>{formatCurrency(calculateGrandTotal())}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </Modal.Body>
            <Modal.Footer style={{ padding: '24px 32px', background: 'white', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={handleCancel}
                  disabled={isLoading}
                  style={{ background: 'transparent', border: '1px solid #cbd5e1', color: '#64748b', fontWeight: '600', fontSize: '15px', padding: '10px 16px', borderRadius: '10px', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '8px' }}
                  onMouseOver={(e) => !isLoading && (e.currentTarget.style.background = '#f1f5f9')}
                  onMouseOut={(e) => !isLoading && (e.currentTarget.style.background = 'transparent')}
                >
                  Close
                </button>
                <button
                  onClick={handleModalClose}
                  disabled={isLoading}
                  style={{ background: 'transparent', border: 'none', color: '#64748b', fontWeight: '600', fontSize: '15px', padding: '10px 16px', borderRadius: '10px', transition: 'background 0.2s', display: 'flex', alignItems: 'center', gap: '8px' }}
                  onMouseOver={(e) => !isLoading && (e.currentTarget.style.background = '#f1f5f9')}
                  onMouseOut={(e) => !isLoading && (e.currentTarget.style.background = 'transparent')}
                >
                  Close & Exit
                </button>
              </div>
              <Button
                onClick={handlePrint}
                disabled={isLoading}
                style={{ background: '#5b57f4', border: 'none', color: 'white', fontWeight: '600', fontSize: '15px', padding: '12px 28px', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '8px', transition: 'background 0.2s', boxShadow: '0 4px 12px rgba(91, 87, 244, 0.2)' }}
                onMouseOver={(e) => !isLoading && (e.currentTarget.style.background = '#4a47d6')}
                onMouseOut={(e) => !isLoading && (e.currentTarget.style.background = '#5b57f4')}
              >
                <i className="fas fa-print"></i>
                {isLoading ? "Processing..." : "Print Receipt"}
                {isKeyboardShortcutsEnabled && <span style={{ background: 'rgba(255,255,255,0.2)', padding: '2px 8px', borderRadius: '6px', fontSize: '12px', marginLeft: '4px' }}>Enter</span>}
              </Button>
            </Modal.Footer>
          </Modal>
        </div>
      </div>
    </div>
  );
}
export default Cash;
